import { useState } from 'react';
import styled from 'styled-components';

import EditDatesModal from 'components/modals/EditDatesModal';
import PermissionGuard from 'components/shared/PermissionGuard';
import { useRehabStates } from 'context/rehabStates';
import { useTranslation } from 'context/translations';
import { simpleDate } from 'lib/date';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import { PERMISSIONS } from 'models/Profile';
import { colors } from 'styles/theme/colors';
import { H3, Label } from 'styles/typography';
import EditIcon from 'svg/EditIcon';

type Props = { patientName: string; locationEpisode: LocationEpisode; episode: Episode };

export default function PatientRehabState({ locationEpisode, patientName, episode }: Props) {
  const [showModal, setShowModal] = useState(false);
  const rehabStates = useRehabStates();
  const { translate } = useTranslation();

  const currentRehabState = episode.currentRehabState;
  const rehabStateText = rehabStates.find((rs) => rs.apiName === currentRehabState.apiName)?.state;

  const showLosChip = episode.trackLos && (locationEpisode.stayDescriptor === 'Past' || !currentRehabState.queue);

  const losText = `${locationEpisode.lengthOfStay} day${locationEpisode.lengthOfStay === 1 ? '' : 's'}`;

  const endDateText =
    locationEpisode.latest && locationEpisode.daysSinceEndDate == 0 ? 'present' : simpleDate(locationEpisode.endDate);

  const headerText =
    locationEpisode.stayDescriptor !== 'Current'
      ? `${locationEpisode.stayDescriptor} Record`
      : rehabStateText || 'Current Stay';

  return (
    <>
      {showModal && (
        <EditDatesModal setShow={setShowModal} patientName={patientName!} locationEpisode={locationEpisode} />
      )}
      <HeadSection>
        <div className='head-section-text'>
          <HeaderContainer>
            <H3>{translate(headerText)}</H3>
            {!episode.archived && !locationEpisode.archived && (
              <PermissionGuard
                permission={PERMISSIONS.rehabStateEdit}
                locationEpisode={locationEpisode}
                episode={episode}>
                <ActionIcon data-cy='clickable' onClick={() => setShowModal(true)}>
                  <EditIcon width={18} height={18} color={colors.black} />
                </ActionIcon>
              </PermissionGuard>
            )}
          </HeaderContainer>
          {locationEpisode.currentRehabState.queue ? (
            <HeadDate>Added on {simpleDate(locationEpisode.currentRehabState.enteredAt)}</HeadDate>
          ) : (
            <HeadDate>
              {simpleDate(locationEpisode.startDate)} - {endDateText}
            </HeadDate>
          )}
        </div>
        {showLosChip && <DayCounterChip $latest={locationEpisode.latest}>{losText}</DayCounterChip>}
      </HeadSection>
    </>
  );
}

const HeadSection = styled.div`
  display: flex;
  padding: 24px;
  align-items: center;
  justify-content: space-between;
  .head-section-text {
    display: flex;
    flex-direction: column;
  }
`;
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const HeadDate = styled(Label)`
  margin-top: 6px;
  color: var(--black-50);
`;

const DayCounterChip = styled(H3)<{ $latest: boolean }>`
  background-color: ${(props) => (props.$latest ? 'var(--secondary-orange)' : 'var(--black-50)')};
  color: white;
  border-radius: 28px;
  white-space: nowrap;
  padding: 6px 11px;
  line-height: 16px;
`;

const ActionIcon = styled.div`
  height: 18px;
  cursor: pointer;
`;
